/** @format */
import { AxiosRequestConfig } from 'axios';

import { client } from '@toucan/client/global/axios';
import { MarketCode } from '@toucan/client/global/market';

import { Basket, GetBasketOptions } from '../config/types';
import { serializeBasket } from '../serializers';

export async function getBasket(
    market: MarketCode,
    options?: GetBasketOptions
): Promise<Basket> {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const { useCachedPrices = false } = options ?? {};
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };
    const url = '/public-api/v2/basket/';

    /**
     * We use POST if we want to get the latest prices. This also updates
     * the basket cache with these prices. We cache the prices at the start
     * of checkout so the users sees the same prices that checkout service will use.
     * As there's some writing to the database in this call, it is a POST
     *
     * A GET method will return the basket items using the cached prices
     */
    if (useCachedPrices) {
        const response = await client.get(url, config);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example.
        return serializeBasket(response.data);
    }

    const response = await client.post(url, {}, config);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example.
    return serializeBasket(response.data.basket);
}

/**
 * Add a product to the basket.
 *
 * @param model the model id
 * @param product the product id
 * @param quantity the quantity of the item to add
 * @param market the market code
 */
export async function addToBasket(
    model: number,
    product: number | undefined,
    quantity: number,
    market: MarketCode
): Promise<Basket> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
        },
    };
    const response = await client.post(
        '/public-api/v2/basket/',
        {
            model,
            product,
            quantity,
        },
        config
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example.
    return serializeBasket(response.data.basket);
}

/**
 * Remove a product from the basket.
 *
 * @param model the model id
 * @param product the product id
 * @param quantity the quantity of the item to remove
 * @param market the market code
 */
export async function removeFromBasket(
    model: number | undefined,
    product: number | undefined,
    quantity: number,
    market: MarketCode
): Promise<Basket> {
    const response = await client.request({
        method: 'delete',
        url: '/public-api/v2/basket/',
        data: {
            model,
            product,
            quantity,
        },
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
        },
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example.
    return serializeBasket(response.data);
}
